import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Users, ChevronRight } from 'lucide-react';
import type { ConsultantHomeData } from '../../../types/aai';
import { aaiApi } from '../../../services/aaiApi';
import { Card, MonoLabel, Banner, Bar, StatCard, StageBand, Avatar } from './ui';

const ConsultantHome: React.FC<{ data: ConsultantHomeData }> = ({ data }) => {
  const s = data.stats;
  // Apply-template flow: pick a template, then a client to apply it to.
  const [applyKey, setApplyKey] = useState<string | null>(null);
  const [clientId, setClientId] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  const applyTemplate = async () => {
    if (!applyKey || !clientId) return;
    setBusy(true); setMsg(null);
    try {
      await aaiApi.applyTemplate(clientId, { template_key: applyKey });
      setMsg('Template applied — the client’s curriculum is set up.');
      setApplyKey(null); setClientId('');
    } catch (e: any) {
      setMsg(e?.response?.data?.detail || 'Could not apply the template.');
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-4">
      <Banner tone="blue" icon={<Users size={16} className="text-brand-500 shrink-0" />}>
        Delivery view. You span every engagement — switch client at the top to drop into any company's program.
      </Banner>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard value={s.engagements} label="Active engagements" />
        <StatCard value={s.people} label="People in programs" />
        <StatCard value={s.sessions} label="Sessions this week" />
        <StatCard value={s.graduated} label="Graduated to self-serve" valueClass="text-emerald-600" />
      </div>

      <div className="bg-white border border-gray-100 rounded-xl overflow-hidden">
        <div className="px-5 py-3.5 border-b border-gray-100 font-bold text-sm">Your client portfolio</div>
        {data.clients.map((c) => (
          <Link
            key={c.id}
            to={`/consultant/aai/${c.id}`}
            state={{ name: c.name }}
            className="flex items-center gap-3.5 px-5 py-3.5 border-t border-gray-50 first:border-0 hover:bg-gray-50 transition-colors"
          >
            <div className="w-9 h-9 rounded-lg bg-brand-500 text-white grid place-items-center font-bold text-sm shrink-0">{c.initials}</div>
            <div className="flex-1 min-w-0"><div className="font-semibold text-sm truncate">{c.name}</div><div className="text-xs text-gray-500">{c.meta}</div></div>
            <StageBand stage={c.stage} />
            <div className="w-32 hidden sm:block">
              <div className="flex justify-between text-[11px] text-gray-500 mb-1"><span className="capitalize">{c.stage}</span><span>{c.pct}</span></div>
              <Bar pct={c.pct} color="brand" track="bg-gray-100" height="h-1.5" />
            </div>
            <ChevronRight size={16} className="text-gray-300 shrink-0" />
          </Link>
        ))}
        {data.clients.length === 0 && <p className="px-5 py-6 text-[13px] text-gray-400">No clients assigned to you yet.</p>}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <MonoLabel className="mb-2.5">Program templates</MonoLabel>
          {data.templates.map((t) => (
            <div key={t.key} className="bg-white rounded-lg px-3.5 py-3 mb-2 last:mb-0 flex justify-between items-center">
              <div><div className="font-semibold text-[13px]">{t.name}</div><div className="text-[11.5px] text-gray-500">{t.meta}</div></div>
              <div className="flex items-center gap-2.5 shrink-0">
                <span className="text-[11px] font-semibold text-brand-600">{t.tag}</span>
                <button onClick={() => { setApplyKey(t.key); setClientId(''); setMsg(null); }}
                  className="text-[11px] font-semibold px-2.5 py-1 rounded-lg bg-brand-50 text-brand-700 hover:bg-brand-100">Apply</button>
              </div>
            </div>
          ))}
          <p className="text-[11.5px] text-gray-400 mt-2.5">Applying sets up a client’s modules &amp; lessons — edit them per client afterwards.</p>
        </Card>
        <Card>
          <MonoLabel className="mb-2.5">This week's delivery</MonoLabel>
          {data.delivery.map((d, i) => (
            <div key={i} className="flex items-center gap-2.5 py-2 border-b border-brand-100 last:border-0">
              <div className="font-sans text-[11px] text-brand-600 font-semibold w-12">{d.day}</div>
              <div className="flex-1"><div className="text-[13px] font-semibold">{d.title}</div><div className="text-[11.5px] text-gray-500">{d.client}</div></div>
            </div>
          ))}
        </Card>
      </div>

      {msg && <div className="text-[12px] text-gray-600 bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">{msg}</div>}

      {applyKey && (
        <div className="fixed inset-0 z-[90] bg-black/30 flex items-center justify-center p-4" onClick={() => !busy && setApplyKey(null)}>
          <div className="bg-white rounded-xl p-5 w-full max-w-sm shadow-xl" onClick={(e) => e.stopPropagation()}>
            <h2 className="text-base font-semibold text-gray-900 mb-1">Apply template</h2>
            <p className="text-[12.5px] text-gray-500 mb-4">Sets up the client’s curriculum (modules &amp; lessons). This replaces any existing curriculum for that client.</p>
            <label className="block text-[11px] font-medium text-gray-500 uppercase tracking-wider mb-1">Client</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm mb-4">
              <option value="">Select a client…</option>
              {data.clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="flex justify-end gap-2">
              <button onClick={() => setApplyKey(null)} disabled={busy} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-700 disabled:opacity-50">Cancel</button>
              <button onClick={applyTemplate} disabled={busy || !clientId} className="text-[12px] font-semibold px-3 py-1.5 rounded-lg bg-brand-500 hover:bg-brand-600 text-white disabled:opacity-50">{busy ? 'Applying…' : 'Apply'}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultantHome;
